import React, { useState, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import _last from 'lodash/last'
import { Box, CardActions, Typography, useTheme, useMediaQuery, Tooltip } from '@mui/material'
import { decodeTrademarkEntity } from '@helpers/string-helper'
import { useProduct } from '@components/product/hooks'
import StrapiLink from '@templates/strapi-cms/content-types/Link'
import { useTest } from '@hooks/useTest'
import ProductPrice from '../../atoms/ProductPrice/ProductTilePrice'
import Ratings from '../../atoms/Ratings/Ratings'
import FavoriteButton from '../../atoms/Button/Favorite'
import ProductColor from '../../atoms/ProductColor'
import StockMessage from '../../atoms/StockMessage/StockMessage'
import ProductTileImage from './ProductTileImage'
import {
  StyledCard,
  StyledCardContent,
  StyledProductTileButton,
  ProductTileButtonWrapper,
  StyledProductAttributes,
  StyledPriceContainer,
  StyledReviewsContainer,
  StyledSaleFlag,
  StyledAvailableColorsTypography,
  TitleAndFlagWrapper,
  StyledTitle,
  CurrentItemFlagWrapper,
  ExtraAvailableColorsWrapper,
  AvailableColorsWrapper,
  StyledAvailabilityTypography,
} from './ProductTileForComparison.styles'

// TODO: Pending the following:
// - Accessibility
// - Finish variants
// - Size variants
// - Various sale flags
// - "+more" logic
// - Polish financing
// - Bullets between sale flags
// - Create storybook component for financeAmount
// - Error messaging

export const generateAdjustedMessage = stockMessageArr => {
  if (!stockMessageArr || !Array.isArray(stockMessageArr)) return []
  return stockMessageArr.map(item => {
    if (/available now|in stock/i.test(item?.message)) {
      item.message = item.message.toUpperCase()
    }
    const replaceRegex = /available as soon as|available after|\.$/i
    if (replaceRegex.test(item?.message)) {
      item.message = item.message.replace(replaceRegex, '')
    }
    return item
  })
}

const defaultAnalytics = {
  favorites: () => {},
  click: () => {},
}

export const handleMouseEnter = (textElementRef, setIsTitleOverflow, isTitleOverflow) => {
  const element = textElementRef.current
  if (element.scrollHeight > element.clientHeight && !isTitleOverflow) {
    setIsTitleOverflow(true)
  } else if (element.scrollHeight <= element.clientHeight && isTitleOverflow) {
    setIsTitleOverflow(false)
  }
}

export default function ProductTileForComparison({
  primary_image,
  alternate_image,
  type,
  productCategory,
  subCategory,
  brand,
  title,
  sku,
  default_price,
  price,
  handleClick,
  sale,
  financeAmount,
  financeNumMonths,
  availableColorVariations,
  isChecked,
  room_image,
  isLoading,
  isError,
  updateFavorite,
  route,
  updateVariant,
  inViewRef,
  showVariations = true,
  strikeThroughPrice = 0,
  strikeThrough = false,
  saleFlagLabel,
  productExtraLabel,
  isDropShipping,
  isFreeShipping,
  isAvailableForRegion,
  onAddToCart,
  renderAddToCartModal,
  trackingData,
  source,
  currentSku,
  comparisonFields,
  product = null,
  isInStock: forceIsInStock,
  showAddToCart: forceShowAddToCart,
}) {
  const {
    isInStock,
    showAddToCart,
    isThankyouDiscount,
    dimensions,
    technology,
    sleepPosition,
    reviewsCount,
    averageRating,
    stockMessage,
  } = useProduct({
    product,
  })
  const textElementRef = useRef()
  const [isTitleOverflow, setIsTitleOverflow] = useState(false)
  const { generateTestId } = useTest()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const showAddToCartOnPLP = forceShowAddToCart || showAddToCart
  const isCurrentItem = currentSku && product?.sku === currentSku

  const variantLimit = isMobile ? 3 : 6

  const onUpdateVariant = selectedSku => updateVariant(selectedSku)

  const availableColorsForRegion = availableColorVariations
  const isFavoriteUnavailable = !showAddToCartOnPLP && source === 'favorites'

  const stockMessageAdjusted = useMemo(() => generateAdjustedMessage(stockMessage), [stockMessage])

  const onFavoriteButtonClick = e => {
    updateFavorite({ remove: isChecked })
  }

  const onAddToCartButton = e => {
    e.stopPropagation()
    e.preventDefault()
    onAddToCart(e)
  }

  const renderCard = () => {
    const adjustedSaleFlagLabel = /in stock & on sale/i.test(saleFlagLabel) && isMobile ? 'SALE' : saleFlagLabel
    const { fields, hasLabels } = comparisonFields

    return (
      <StyledCard square variant="outlined" ref={inViewRef} isCurrentItem={isCurrentItem}>
        {isError && <div>Error {JSON.stringify(isError)}</div>}
        {sale && <StyledSaleFlag label={adjustedSaleFlagLabel} isCurrentItem={isCurrentItem} />}
        {renderAddToCartModal()}
        {/* Product Images */}
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={isCurrentItem && { '& .MuiCardActionArea-root': { cursor: 'inherit' } }}>
            <ProductTileImage
              onClick={!isCurrentItem ? handleClick : null}
              primaryImage={type === 'room' && room_image ? room_image : primary_image}
              alternateImage={alternate_image}
              title={title}
              showAddToCartIcon={isAvailableForRegion && productCategory !== 'mattress bedding'}
              onAddToCartClick={onAddToCart}
            />
          </Box>
          <StyledCardContent>
            {/* Product Title */}
            <StyledProductAttributes>
              <tbody>
                <tr>
                  <td>
                    {title && (
                      <TitleAndFlagWrapper data-testid={generateTestId('product-tile-comparison', sku)}>
                        {isCurrentItem && <CurrentItemFlagWrapper>Current Item</CurrentItemFlagWrapper>}
                        <Tooltip title={title} disableHoverListener={!isTitleOverflow} disableTouchListener>
                          <StyledTitle
                            isCurrentItem={isCurrentItem}
                            ref={textElementRef}
                            onMouseEnter={() => handleMouseEnter(textElementRef, setIsTitleOverflow, isTitleOverflow)}
                          >
                            {decodeTrademarkEntity(title)}
                          </StyledTitle>
                        </Tooltip>
                      </TitleAndFlagWrapper>
                    )}
                  </td>
                </tr>
                {fields?.brand && (
                  <tr>
                    <td>
                      {/* Product Brand */}
                      {brand && (
                        <>
                          <Typography>
                            {hasLabels && 'BRAND: '}
                            <Typography component="span">{brand}</Typography>
                          </Typography>
                        </>
                      )}
                    </td>
                  </tr>
                )}
                {fields?.colors && (
                  <tr>
                    <td>
                      {availableColorsForRegion?.length > 0 && showVariations && (
                        <AvailableColorsWrapper>
                          {availableColorsForRegion?.map(
                            (variant, key) =>
                              key < variantLimit && (
                                <ProductColor
                                  disableEventPropagation
                                  key={`color-variant-${variant.sku}`}
                                  sx={{
                                    marginRight: '5px',
                                  }}
                                  width={isMobile ? '30px' : '35px'}
                                  height={isMobile ? '30px' : '35px'}
                                  isSelected={sku === variant.sku}
                                  color={variant}
                                  onClick={e => onUpdateVariant(e)}
                                />
                              ),
                          )}
                          {availableColorsForRegion?.length > variantLimit && (
                            <ExtraAvailableColorsWrapper>
                              <StyledAvailableColorsTypography>
                                +{availableColorsForRegion?.length - variantLimit}
                              </StyledAvailableColorsTypography>
                            </ExtraAvailableColorsWrapper>
                          )}
                        </AvailableColorsWrapper>
                      )}
                    </td>
                  </tr>
                )}
                {fields?.dimensions && dimensions && (
                  <tr>
                    <td>
                      {dimensions && hasLabels && (
                        <Typography>
                          {'DIMENSIONS: '}
                          <Typography component="span">{dimensions}</Typography>
                        </Typography>
                      )}
                      {dimensions && !hasLabels && <Typography component="span">{dimensions}</Typography>}
                    </td>
                  </tr>
                )}
                {fields?.technology && (
                  <tr>
                    <td>
                      {technology && (
                        <Typography>
                          {hasLabels && 'TECHNOLOGY: '}
                          {isMobile && <br />}
                          <Typography component="span" sx={{ ...(isMobile && { marginLeft: '0!important' }) }}>
                            {technology}
                          </Typography>
                        </Typography>
                      )}
                    </td>
                  </tr>
                )}

                {fields?.sleepPosition && (
                  <tr>
                    <td>
                      {sleepPosition && (
                        <Typography>
                          {hasLabels && 'SLEEP POSITION: '}
                          <Typography component="span">{sleepPosition?.replaceAll('/', ' / ')}</Typography>
                        </Typography>
                      )}
                    </td>
                  </tr>
                )}
                {fields?.reviews && (
                  <tr>
                    <td>
                      {reviewsCount > 0 && (
                        <StyledReviewsContainer sx={{ '& .MuiStack-root': { padding: 0 } }}>
                          <Ratings name="half-rating-read" readOnly max={5} precision={0.2} value={averageRating} />
                          <Typography marginLeft="0.6rem">{`${reviewsCount} reviews`}</Typography>
                        </StyledReviewsContainer>
                      )}
                    </td>
                  </tr>
                )}
                {fields?.productExtraLabel && (
                  <tr>
                    <td>
                      <Typography>
                        <Typography component="span">{productExtraLabel}</Typography>
                      </Typography>
                    </td>
                  </tr>
                )}
                {fields?.availability && (
                  <tr>
                    <td>
                      <StyledAvailabilityTypography component="div">
                        {/* AVAILABILITY MESSAGE */}
                        <StockMessage stockMessage={stockMessageAdjusted} />
                      </StyledAvailabilityTypography>
                    </td>
                  </tr>
                )}
                {fields?.price && (
                  <tr>
                    <td>
                      <StyledPriceContainer>
                        {/* Product Price */}
                        {isAvailableForRegion && (
                          <ProductPrice
                            fontSize={isMobile ? '19px' : '23px'}
                            price={price}
                            sale={sale}
                            strikeThrough={strikeThrough}
                            strikeThroughPrice={strikeThroughPrice}
                            highlightPrice={saleFlagLabel?.toLowerCase() === 'last chance' || isThankyouDiscount}
                          />
                        )}
                        {/* Product Financing Message */}
                        {financeAmount >= 5 && (
                          <Typography color={theme.palette.error.main}>
                            {`$${financeAmount}/mo. with ${financeNumMonths} months financing*`}
                          </Typography>
                        )}
                      </StyledPriceContainer>
                    </td>
                  </tr>
                )}
              </tbody>
            </StyledProductAttributes>
            {fields?.addToCart && (
              <ProductTileButtonWrapper>
                <StyledProductTileButton
                  onClick={onAddToCartButton}
                  color="primary"
                  fullWidth
                  variant="contained"
                  aria-label={`Add To Cart for ${title}`}
                >
                  ADD TO CART
                </StyledProductTileButton>
              </ProductTileButtonWrapper>
            )}
          </StyledCardContent>
        </Box>
        <CardActions
          className="favoriteButtonContainer"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: '8px',
          }}
        >
          <FavoriteButton checked={isChecked} handleClick={onFavoriteButtonClick} size="small" sku={sku} />
        </CardActions>
      </StyledCard>
    )
  }

  if (isCurrentItem) {
    return <>{renderCard()}</>
  }

  return (
    <StrapiLink
      className="product-image-link"
      data={{
        InternalUrl: route,
        testId: `product-tile-link:${sku}`,
      }}
      trackingData={trackingData}
      disableUnderline
    >
      {renderCard()}
    </StrapiLink>
  )
}

ProductTileForComparison.propTypes = {
  primary_image: PropTypes.string,
  alternate_image: PropTypes.string,
  room_image: PropTypes.string,
  route: PropTypes.string,
  type: PropTypes.string,
  productCategory: PropTypes.string,
  subCategory: PropTypes.array,
  brand: PropTypes.string,
  title: PropTypes.string,
  sku: PropTypes.string,
  default_price: PropTypes.number,
  price: PropTypes.number,
  strikeThroughPrice: PropTypes.number,
  strikeThrough: PropTypes.bool,
  sale: PropTypes.bool,
  financeAmount: PropTypes.number,
  financeNumMonths: PropTypes.number,
  availableColorVariations: PropTypes.array,
  handleClick: PropTypes.func,
  updateVariant: PropTypes.func,
  isChecked: PropTypes.bool,
  inViewRef: PropTypes.func.isRequired /** Required for InView logic of useProductQuery */,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  updateFavorite: PropTypes.func,
  showVariations: PropTypes.bool,
  saleFlagLabel: PropTypes.string,
  productExtraLabel: PropTypes.string,
  isDropShipping: PropTypes.bool,
  isFreeShipping: PropTypes.bool,
  isAvailableForRegion: PropTypes.bool,
  onAddToCart: PropTypes.func,
  renderAddToCartModal: PropTypes.func,
  trackingData: PropTypes.object,
  source: PropTypes.string,
  product: PropTypes.object,
  isInStock: PropTypes.bool,
  showAddToCart: PropTypes.bool,
  currentSku: PropTypes.string,
  comparisonFields: PropTypes.object,
}
